import { Eye, FileText, ShieldCheck, Sparkles } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero, SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { PineappleIcon } from "@/components/PineappleIcon";
import { IMAGES } from "@/data/content";

const VALUES = [
  { icon: ShieldCheck, title: "Independent", desc: "We answer only to you — never to developers, contractors or agents. Our findings are yours alone." },
  { icon: Eye, title: "Meticulous", desc: "Every tile tapped, every point tested, every wall read with a straightedge. Nothing is too small to flag." },
  { icon: FileText, title: "Transparent", desc: "A full photo report within 24 hours, mapped to blue-tape markings your BSC can act on immediately." },
  { icon: Sparkles, title: "Auspicious", desc: "We believe a home inspection should end the way a key collection begins — with prosperity welcomed in." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Us | The Lobang & Pineapple Story — Lobang Home Inspection"
        description="Why we're called Lobang, what the prosperity pineapple means to us, and how we inspect Singapore's new homes with independent, engineering-grade rigour."
      />
      <PageHero
        eyebrow="About Us"
        title="Singapore's friendliest defect detectives"
        copy="A boutique inspection team pairing engineering-grade rigour with the warmth of a housewarming gift."
      />

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="about-intro">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">Who We Are</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-charcoal sm:text-4xl">
              Inspectors first, salespeople never
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-charcoal-soft sm:text-base">
              Lobang Home Inspection was founded by inspectors who kept hearing the same story: families collecting keys
              to their dream BTO, only to discover hollow tiles, ponding toilets and dead sockets weeks after moving in.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-charcoal-soft sm:text-base">
              We exist to catch those defects while the developer is still obliged to fix them — with CONQUAS-informed
              methods, professional tools and a report your BSC cannot ignore. BTO flats, condos and landed homes,
              islandwide across Singapore.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="gold-ring overflow-hidden rounded-[2rem] shadow-luxury">
              <img src={IMAGES.heroSecondary} alt="Warm champagne-toned Singapore interior" className="h-80 w-full object-cover sm:h-96" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-charcoal px-5 py-20 sm:px-8 sm:py-28" data-testid="pineapple-story">
        <PineappleIcon className="pointer-events-none absolute -left-12 bottom-0 h-80 w-56 -rotate-12 text-gold/5" strokeWidth={0.8} />
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="gold-ring overflow-hidden rounded-[2rem] shadow-luxury">
              <img src={IMAGES.pineapple} alt="Lobang Home Inspection company logo with its signature gold pineapple" className="h-80 w-full bg-cream object-contain sm:h-96" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="eyebrow">The Legend of 'Lobang'</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-ivory sm:text-4xl">
              旺来 — the prosperity pineapple
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-ivory/70 sm:text-base">
              In Singlish, a <em className="text-gold-light">lobang</em> is a valuable opportunity — the insider tip you
              share with a friend. And in a cherished Singaporean tradition, new homeowners roll a pineapple into their
              unit on key collection day: <em className="text-gold-light">Ong Lai</em> (旺来) in Hokkien, "prosperity,
              come in."
            </p>
            <p className="mt-4 text-sm leading-relaxed text-ivory/70 sm:text-base">
              We named ourselves after both ideas. A thorough defect inspection is the best lobang a new homeowner can
              get — and like the pineapple, we arrive before anyone else, making sure your new sanctuary begins with
              good fortune and zero hidden flaws.
            </p>
            <div className="mt-7 flex items-center gap-3" aria-hidden="true">
              <span className="h-px w-10 bg-gold/40" />
              <PineappleIcon className="h-6 w-5 text-gold" />
              <span className="h-px w-10 bg-gold/40" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 py-20 sm:px-8 sm:py-28" data-testid="about-values">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="What We Stand For"
            title="Four values in every visit"
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-border bg-white p-7 shadow-luxury transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cream text-gold-dark">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-charcoal">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal-soft">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Let us be your first lobang."
        copy="Key collection coming up? WhatsApp us the date and we'll make sure prosperity — and nothing else — rolls into your new home."
      />
    </>
  );
}
